/**
 * @fileoverview Share Session Composable
 *
 * Wraps the share session store for share portal authentication.
 * Provides reactive state and helper methods for external user sessions.
 */

import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useShareSessionStore } from '@/stores/shareSession'
import { useConsoleSessionsStore } from '@/stores/consoleSessions'

/**
 * Share session composable with enhanced utilities
 *
 * Provides reactive session state and helper methods for share portal.
 * Manages external user authentication and session validation.
 *
 * @returns {Object} Share session state and methods
 * @example
 * import { useShareSession } from '@/composables/useShareSession'
 *
 * const {
 *   isAuthenticated,
 *   sessionTimeRemaining,
 *   login,
 *   logout
 * } = useShareSession()
 *
 * if (isExpired.value) {
 *   await logout()
 * }
 */
export function useShareSession() {
  const shareSessionStore = useShareSessionStore()
  const consoleSessionsStore = useConsoleSessionsStore()
  const router = useRouter()

  // Reactive state from store
  const shareId = computed(() => shareSessionStore.shareId)
  const labId = computed(() => shareSessionStore.labId)
  const labName = computed(() => shareSessionStore.labName)
  const expiresAt = computed(() => shareSessionStore.expiresAt)
  const isAuthenticated = computed(() => shareSessionStore.isAuthenticated)
  const isLoading = computed(() => shareSessionStore.isLoading)
  const error = computed(() => shareSessionStore.error)

  // Computed from store getters
  const sessionTimeRemaining = computed(() => shareSessionStore.sessionTimeRemaining)
  const isExpired = computed(() => shareSessionStore.isExpired)

  /**
   * Format time remaining as human-readable string
   *
   * Converts seconds to "Xh Ym" or "Xm Ys" format.
   *
   * @returns {string} Formatted time remaining
   * @example
   * const { timeRemainingFormatted } = useShareSession()
   * // "2h 30m" or "45m 20s" or "Expired"
   */
  const timeRemainingFormatted = computed(() => {
    const seconds = sessionTimeRemaining.value

    if (seconds <= 0) {
      return 'Expired'
    }

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }

    if (minutes > 0) {
      return `${minutes}m ${secs}s`
    }

    return `${secs}s`
  })

  /**
   * Login to share portal with credentials
   *
   * Authenticates external user and establishes session.
   *
   * @param {string} id - Share UUID
   * @param {string} username - Guacamole username
   * @param {string} password - Guacamole password
   * @returns {Promise<void>}
   * @throws {Error} If authentication fails
   * @example
   * const { login } = useShareSession()
   *
   * try {
   *   await login(shareId, username, password)
   *   router.push(`/share/${shareId}`)
   * } catch (error) {
   *   console.error('Login failed:', error.message)
   * }
   */
  async function login(id, username, password) {
    try {
      await shareSessionStore.login(id, username, password)
    } catch (err) {
      console.error('Share login failed:', err)
      throw err
    }
  }

  /**
   * Logout and clear share session
   *
   * Clears session token and redirects to share login page.
   *
   * @example
   * const { logout } = useShareSession()
   *
   * await logout()
   * // Redirected to /share/:id
   */
  async function logout() {
    const currentShareId = shareId.value

    consoleSessionsStore.closeAll()

    // Clear session
    shareSessionStore.logout()

    // Redirect to share login page
    if (currentShareId) {
      router.push(`/share/${currentShareId}`)
    } else {
      router.push('/')
    }
  }

  /**
   * Check if current session is valid
   *
   * Verifies session token exists and hasn't expired.
   * Returns false and redirects to login if invalid.
   *
   * @returns {boolean} True if session is valid
   * @example
   * const { checkSession } = useShareSession()
   *
   * onMounted(() => {
   *   if (!checkSession()) {
   *     // Will redirect to login automatically
   *   }
   * })
   */
  function checkSession() {
    const isValid = shareSessionStore.checkSession()

    if (!isValid && shareId.value) {
      // Session invalid, redirect to login
      router.push(`/share/${shareId.value}/login`)
    }

    return isValid
  }

  /**
   * Load public share information
   *
   * Fetches share details for display on login page.
   * This is a public endpoint - no authentication required.
   *
   * @param {string} id - Share UUID
   * @returns {Promise<Object>} Share information
   * @example
   * const { loadShareInfo } = useShareSession()
   *
   * onMounted(async () => {
   *   const shareId = route.params.id
   *   const info = await loadShareInfo(shareId)
   *   console.debug(`Lab: ${info.lab_name}`)
   * })
   */
  async function loadShareInfo(id) {
    try {
      return await shareSessionStore.loadShareInfo(id)
    } catch (err) {
      console.error('Failed to load share info:', err)
      throw err
    }
  }

  /**
   * Check if session is about to expire
   *
   * Returns true if less than 5 minutes remaining.
   * Useful for showing expiration warnings.
   *
   * @returns {boolean} True if session expiring soon
   * @example
   * const { isExpiringSoon } = useShareSession()
   *
   * if (isExpiringSoon.value) {
   *   showWarning('Session expiring soon')
   * }
   */
  const isExpiringSoon = computed(() => {
    const seconds = sessionTimeRemaining.value
    return seconds > 0 && seconds < 300 // Less than 5 minutes
  })

  return {
    // Reactive state
    shareId,
    labId,
    labName,
    expiresAt,
    isAuthenticated,
    isLoading,
    error,

    // Computed state
    sessionTimeRemaining,
    timeRemainingFormatted,
    isExpired,
    isExpiringSoon,

    // Methods
    login,
    logout,
    checkSession,
    loadShareInfo
  }
}
